
import React from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNotifications } from '@/hooks/useNotifications';
import NotificationItem from './NotificationItem';
import { ClearNotificationsDialog } from './ClearNotificationsDialog';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';

const NotificationDropdown = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAllNotifications } = useNotifications();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);

  const recentNotifications = notifications.slice(0, 5);

  const handleBellClick = () => setOpen(prev => !prev);

  const bellContent = (
    <>
      <Bell size={24} className="text-muted-foreground hover:text-foreground transition-colors" />
      {unreadCount > 0 && (
        <span className="absolute top-0 right-0 bg-primary text-primary-foreground text-[10px] font-medium rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </>
  );

  const header = (
    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b bg-muted/30">
      <span className="font-semibold text-foreground">Notifications</span>
      <div className="flex items-center gap-2">
        {unreadCount > 0 && (
          <Badge variant="default" className="text-[10px] px-2 py-0.5">
            {unreadCount} new
          </Badge>
        )}
        {unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs text-primary hover:text-primary min-h-[44px] sm:min-h-0"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              markAllAsRead();
            }}
          >
            <CheckCheck className="h-3.5 w-3.5 mr-1" />
            Mark all read
          </Button>
        )}
        {notifications.length > 0 && (
          <ClearNotificationsDialog
            onClear={clearAllNotifications}
            variant="ghost"
            size="sm"
            showLabel={!isMobile}
          />
        )}
      </div>
    </div>
  );


  const body = recentNotifications.length > 0 ? (
    <div className={isMobile ? 'max-h-[60dvh] overflow-y-auto' : 'max-h-[min(60vh,400px)] overflow-y-auto'}>
      {recentNotifications.map(notification => (
        <div key={notification.id} className="border-b last:border-b-0" onClick={() => setOpen(false)}>
          <NotificationItem
            notification={notification}
            onMarkAsRead={markAsRead}
            showIcon={true}
          />
        </div>
      ))}

      <div className="p-2 border-t bg-muted/20">
        <Button
          variant="ghost"
          className="w-full justify-center text-sm text-muted-foreground hover:text-foreground min-h-[44px]"
          onClick={() => {
            setOpen(false);
            navigate('/notifications');
          }}
        >
          View All Notifications
        </Button>
      </div>
    </div>
  ) : (
    <div className="p-8 text-center">
      <Bell className="h-10 w-10 mx-auto mb-3 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">No notifications yet</p>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Notifications"
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={handleBellClick}
          className="relative h-11 w-11 min-h-[44px] min-w-[44px] p-0 [&_svg]:size-6"
        >
          {bellContent}
        </Button>
        <SheetContent
          side="bottom"
          className="p-0 gap-0 max-h-[80dvh] overflow-hidden rounded-t-2xl bg-card"
        >
          <SheetTitle className="sr-only">Notifications</SheetTitle>
          {header}
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Notifications"
          className="relative h-11 w-11 min-h-[44px] min-w-[44px] p-0 [&_svg]:size-6"
        >
          {bellContent}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        collisionPadding={{ top: 64, bottom: 24, left: 8, right: 8 }}
        className="w-[min(24rem,calc(100vw-1rem))] p-0 bg-card border shadow-lg"
      >
        {header}
        {body}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationDropdown;
